import React from 'react';
import { observer } from 'mobx-react';

import { CustomChartBase, InjectedStyledComponent } from '../../../shared/index.js';
import Legend from '../../base/Legend/Legend.js';
import { ILegendItemProps } from '../../base/Legend/models/index.js';

import BottomSeriesPoint from './components/BottomSeriesPoint/index.js';
import { IBottomSeriesPointProps } from './components/BottomSeriesPoint/models/index.js';
import TopSeriesPoint from './components/TopSeriesPoint/index.js';
import { ITopSeriesPointProps } from './components/TopSeriesPoint/models/index.js';
import { IBlock, IBlockRangeChartConfiguration, IBlockRangeChartProps, IBlockRangeChartState } from './models/index.js';
import Block from './styledComponents/Block.js';
import Blocks from './styledComponents/Blocks.js';
import BlockValue from './styledComponents/BlockValue.js';
import Wrapper from './styledComponents/Wrapper.js';
import { BLOCK_RANGE_CHART_CATEGORY_KEY, BLOCK_RANGE_CHART_COMPONENT_KEY } from './BlockRangeChart.meta.js';

export class BlockRangeChart extends CustomChartBase<
	IBlockRangeChartProps,
	IBlockRangeChartState,
	IBlockRangeChartConfiguration
> {
	categoryKey = BLOCK_RANGE_CHART_CATEGORY_KEY;
	componentKey = BLOCK_RANGE_CHART_COMPONENT_KEY;

	constructor(props: IBlockRangeChartProps) {
		super(props);
	}

	get blocks(): IBlock[] {
		const blocks: IBlock[] = [];
		const { min = 1, max = 10, increment = 1 } = this.props;
		for (let i = min; i <= max; i += increment) {
			blocks.push({
				start: i,
				end: i + increment,
			});
		}
		return blocks;
	}

	getBlockSeries = (block: IBlock) => {
		const { start, end } = block;
		const { series = [] } = this.props;
		return series.filter((s) => s.value >= start && s.value < end);
	};

	getLegendItems = (): ILegendItemProps[] => {
		let items: ILegendItemProps[] = [];
		this.blocks.forEach((block) => {
			const blockSeries = this.getBlockSeries(block);
			if (blockSeries.length > 0) {
				items = [
					...items,
					...blockSeries.map((serie) => {
						return {
							label: { value: serie.name },
							value: { value: serie.value.toString() },
							color: { color: serie.indicatorSvgProps && serie.indicatorSvgProps.color },
						} as ILegendItemProps;
					}),
				];
			}
		});
		return items;
	};

	render() {
		const { className, blockValueKey = 'start' } = this.props;
		const blocks = this.blocks;
		const legendProps = this.legendProps;
		const legendPosition = this.legendPosition;
		return (
			<Wrapper className={className}>
				{legendProps && legendPosition === 'top' && <Legend {...legendProps} />}
				<Blocks>
					{blocks.map((block) => {
						const blockSeries = this.getBlockSeries(block);
						const blockValue = block[blockValueKey];
						const { start, end } = block;
						const blockKey = `start_${start}_end_${end}`;
						return (
							<Block key={blockKey}>
								<BlockValue>{blockValue}</BlockValue>
								{blockSeries.map((seriesPoint, seriesPointIndex) => {
									const {
										value,
										name,
										position = 'bottom',
										indicatorType = 'asset',
										indicatorSvgProps,
									} = seriesPoint;
									const key = `${name}_${seriesPointIndex}`;
									const seriesPointProps: ITopSeriesPointProps | IBottomSeriesPointProps = {
										position,
										value,
										start,
										end,
										type: indicatorType,
										svgProps: indicatorSvgProps,
									};
									if (position === 'top') {
										seriesPointProps.imageProps = {
											asset: 'charting.blockRangeChart.seriesPointTop',
										};
										return <TopSeriesPoint key={key} {...seriesPointProps} />;
									}
									if (position === 'bottom') {
										seriesPointProps.imageProps = {
											asset: 'charting.blockRangeChart.seriesPointBottom',
										};
										return <BottomSeriesPoint key={key} {...seriesPointProps} />;
									}
								})}
							</Block>
						);
					})}
				</Blocks>
				{legendProps && legendPosition === 'bottom' && <Legend {...legendProps} />}
			</Wrapper>
		);
	}
}

export default InjectedStyledComponent(
	observer(BlockRangeChart),
	BLOCK_RANGE_CHART_CATEGORY_KEY,
	BLOCK_RANGE_CHART_COMPONENT_KEY,
);
